// Import conditionnel selon l'environnement
import { loadPDFLib } from "./pdf-lib-cdn-loader";

// Variables globales pour pdf-lib
let PDFDocument: any, rgb: any, StandardFonts: any;

// Fonction pour initialiser les imports selon l'environnement
async function initializePdfLib() {
  try {
    const pdfLib = await loadPDFLib();
    PDFDocument = pdfLib.PDFDocument;
    rgb = pdfLib.rgb;
    StandardFonts = pdfLib.StandardFonts;
  } catch (error) {
    console.error("Erreur lors de l'initialisation de pdf-lib:", error);
    throw error;
  }
}

import {
  ConfigPreset,
  FontFamily,
  FontVariants,
  Language,
  PageSize,
  PaperFormat,
  PdfGeneratorOptions,
  getLatestTdaVersion,
  TdaRulesData,
  TdaVersion,
  UserPdfOptions,
} from "../types/pdf-generator";

import { generatePageFormat } from "./docUtil";
import { RulesGenerator } from "./rulesGenerator";

/**
 * Mappe les familles de polices vers les 4 variantes StandardFonts disponibles
 */
const getFontMapping = (fontFamily: FontFamily) => {
  const fontMappings = {
    TimesRoman: {
      regular: StandardFonts.TimesRoman,
      bold: StandardFonts.TimesRomanBold,
      italic: StandardFonts.TimesRomanItalic,
      boldItalic: StandardFonts.TimesRomanBoldItalic,
    },
    Helvetica: {
      regular: StandardFonts.Helvetica,
      bold: StandardFonts.HelveticaBold,
      italic: StandardFonts.HelveticaOblique,
      boldItalic: StandardFonts.HelveticaBoldOblique,
    },
    Courier: {
      regular: StandardFonts.Courier,
      bold: StandardFonts.CourierBold,
      italic: StandardFonts.CourierOblique,
      boldItalic: StandardFonts.CourierBoldOblique,
    },
  };

  return fontMappings[fontFamily] || fontMappings["Helvetica"];
};

/**
 * Générateur PDF pour les règles TDA
 * Optimisé pour Vite.js avec gestion moderne des assets
 */
export class TdaPdfGenerator {
  private readonly defaultOptions: Partial<PdfGeneratorOptions>;

  private rulesGenerator!: RulesGenerator;
  private pdfDoc: any = null;

  constructor() {
    this.defaultOptions = {
      logoPath: "/images/TDA-logo128.png",
      marginTopFontSize: 10,
      marginTopColor: { r: 0.5, g: 0.5, b: 0.5 }, // Sera converti en rgb() plus tard
      marginBottomFontSize: 9,
      marginBottomColor: { r: 0.5, g: 0.5, b: 0.5 }, // Sera converti en rgb() plus tard
      borderWidth: 10,
      borderOpacity: 0.4,
    };
  }

  /**
   * Charge un fichier JSON des règles TDA
   */
  async loadTdaRules(
    version: TdaVersion = getLatestTdaVersion(),
    lang: Language = "fr"
  ): Promise<TdaRulesData> {
    try {
      // Utiliser l'endpoint PHP sécurisé pour accéder aux fichiers JSON
      const pathname = `/api/rules.php?version=${version}&lang=${lang}`;
      const response = await fetch(pathname);
      if (!response.ok) {
        throw new Error(`Impossible de charger les règles: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Erreur lors du chargement des règles:", error);
      throw error;
    }
  }

  /**
   * Charge le logo TDA
   */
  async loadLogo(logoPath: string): Promise<ArrayBuffer | null> {
    try {
      // En développement, utiliser le proxy Vite qui redirige vers localhost:80
      // En production, utiliser les URLs relatives
      const response = await fetch(logoPath);
      if (!response.ok) {
        throw new Error(`Impossible de charger le logo: ${response.status}`);
      }
      return await response.arrayBuffer();
    } catch (error) {
      console.error("Erreur lors du chargement du logo:", error);
      return null;
    }
  }

  /**
   * Applique un preset de configuration
   */
  applyPreset(preset: ConfigPreset): Partial<PdfGeneratorOptions> {
    const presets: Record<ConfigPreset, Partial<PdfGeneratorOptions>> = {
      basic: {
        paperFormat: "US-Letter",
        orientation: "portrait",
        textAlign: "left",
        fontFamily: "TimesRoman",
        includeProcedures: true,
        includeGlossary: true,
        includeExamples: true,
        examples: {
          inline: false,
          fontSize: 11,
          lineSpacing: 1.2,
          marginX: 40,
          marginY: 10,
          bgColor: "#f9f9f2",
          borderColor: "#e6e680",
          radius: 10,
          opacity: 0.8,
        },
        includeIndex: false,
        ruleFontSize: 11,
        textOffset: 0,
        columnCount: 1,
      },
      alinekarl: {
        paperFormat: "A4",
        orientation: "portrait",
        textAlign: "justify",
        fontFamily: "Helvetica",
        includeProcedures: true,
        includeGlossary: true,
        includeExamples: true,
        examples: {
          inline: true,
          fontSize: 11,
          lineSpacing: 1.2,
          marginX: 4,
          marginY: 10,
          bgColor: "#f9f9f2",
          borderColor: "#e6e680",
          radius: 10,
          opacity: 0.8,
        },
        includeIndex: true,
        indexAt: "start",
        indexOptions: {
          lineSpacing: 1.15,
          titleRuleSize: 12,
          sectionRuleSize: 13,
          chapterRuleSize: 14,
          columnCount: 2,
          fontFamily: "TimesRoman",
        },
        ruleFontSize: 12,
        columnCount: 1,
      },
      short: {
        paperFormat: "A4",
        orientation: "landscape",
        textAlign: "justify",
        fontFamily: "Helvetica",
        includeProcedures: false,
        includeGlossary: false,
        includeExamples: false,
        includeIndex: false,
        mainTitleFontSize: 14,
        chapterFontSize: 11,
        ruleTitleFontSize: 11,
        ruleFontSize: 10,
        columnCount: 3,
        logoWidth: 100,
        textOffset: 4,
      },
    };

    return presets[preset] || {};
  }

  /**
   * Génère le PDF des règles TDA
   */
  async generatePdf(userOptions: UserPdfOptions = {}): Promise<Uint8Array> {
    // Initialiser pdf-lib selon l'environnement
    await initializePdfLib();

    // Vérifier que pdf-lib est disponible
    if (!PDFDocument || !rgb || !StandardFonts) {
      throw new Error(
        "pdf-lib n'est pas disponible. Vérifiez l'environnement."
      );
    }

    // Convertir les couleurs par défaut en format rgb()
    const defaultOptions = {
      ...this.defaultOptions,
      marginTopColor: rgb(0.5, 0.5, 0.5),
      marginBottomColor: rgb(0.5, 0.5, 0.5),
    };

    // 1. Récupérer les options de format de page
    const paperFormat: PaperFormat =
      userOptions.paperFormat || defaultOptions.paperFormat || "A4";

    // document size
    const [pageWidth, pageHeight] = generatePageFormat(
      paperFormat,
      userOptions.orientation
    );

    const pageSize: PageSize = {
      width: pageWidth,
      height: pageHeight,
      marginTop: userOptions.topMargin || 50,
      marginBottom: userOptions.bottomMargin || 50,
      marginLeft: userOptions.leftMargin || 40,
      marginRight: userOptions.rightMargin || 40,
    };

    // 2. Créer le document PDF
    const pdfDoc = await PDFDocument.create();
    this.pdfDoc = pdfDoc;

    // 3. Fusionner les options préliminaires pour obtenir la fontFamily
    const preliminaryOptions = {
      ...defaultOptions,
      ...userOptions,
    };

    // 4. Charger les 4 variantes de police selon la fontFamily sélectionnée
    const fontMapping = getFontMapping(
      preliminaryOptions.fontFamily || "Helvetica"
    );
    const fonts = {
      regular: await pdfDoc.embedFont(fontMapping.regular),
      bold: await pdfDoc.embedFont(fontMapping.bold),
      italic: await pdfDoc.embedFont(fontMapping.italic),
      boldItalic: await pdfDoc.embedFont(fontMapping.boldItalic),
      graphic: await pdfDoc.embedFont(StandardFonts.ZapfDingbats),
      symbol: await pdfDoc.embedFont(StandardFonts.Symbol),
    };

    if (
      preliminaryOptions.includeIndex &&
      preliminaryOptions.indexOptions &&
      preliminaryOptions.indexOptions.fontFamily
    ) {
      let indexFonts: FontVariants;
      if (
        preliminaryOptions.indexOptions.fontFamily ===
        preliminaryOptions.fontFamily
      ) {
        indexFonts = {
          ...fonts,
          ...preliminaryOptions.indexOptions.fonts,
        };
      } else {
        const fontMapping = getFontMapping(
          preliminaryOptions.indexOptions.fontFamily
        );
        const newFonts = {
          regular: await pdfDoc.embedFont(fontMapping.regular),
          bold: await pdfDoc.embedFont(fontMapping.bold),
        };
        indexFonts = { ...fonts, ...newFonts };
      }
      userOptions.indexOptions!.fonts = indexFonts;
    }

    // 5. Fusionner toutes les options
    const options: PdfGeneratorOptions = {
      ...defaultOptions,
      ...userOptions,
      paperFormat,
      pageSize,
      fonts,
    } as PdfGeneratorOptions;

    // Appliquer un preset si spécifié
    if (options.configPreset) {
      const presetOptions = this.applyPreset(options.configPreset);
      Object.assign(options, presetOptions, userOptions);
    }

    try {
      // 1. Charger les données
      const rulesData = await this.loadTdaRules(
        options.version || getLatestTdaVersion(),
        options.lang
      );

      if (rulesData.meta.logo_path) {
        options.logoPath = rulesData.meta.logo_path;
      }
      if (rulesData.meta.logo_ratio) {
        options.logoRatio = rulesData.meta.logo_ratio;
      }

      // charger le logo
      const logoBuffer = await this.loadLogo(
        options.logoPath || "/images/TDA-logo128.png"
      );

      // Créer le writer de règles
      this.rulesGenerator = new RulesGenerator(
        pdfDoc,
        options,
        rulesData,
        logoBuffer
      );

      // Ajouter la version
      const version = rulesData.meta?.version || "";
      const pdfTitle = (rulesData.meta?.pdf_title || "TDA {version}").replace(
        "{version}",
        version
      );
      // 4. Générer les pages
      await this.generatePages(options, pdfTitle, rulesData.meta.footer);

      // 5. Retourner les bytes du PDF
      return await pdfDoc.save();
    } catch (error) {
      console.error("Erreur lors de la génération du PDF:", error);
      throw error;
    }
  }

  /**
   * Génère les pages du PDF
   */
  private async generatePages(
    options: PdfGeneratorOptions,
    pdfTitle: string,
    footer?: string[]
  ): Promise<void> {
    // Générer les règles
    await this.generateRules();

    // Ajouter la pagination
    this.addPagination(options);

    // Générer l'index si demandé en fin de document
    if (options.includeIndex) {
      await this.rulesGenerator.generateIndex();
    }

    // Générer les liens
    this.rulesGenerator.generateLinks();

    // Ajouter le header et le footer sur chaque page
    this.addHeaderAndFooter(options, pdfTitle, footer);
  }

  /**
   * Génère les règles du document
   */
  private async generateRules(): Promise<void> {
    if (!this.pdfDoc) {
      throw new Error("PDF document not initialized");
    }

    await this.rulesGenerator.generateRules();

    // Examples appendix, then the glossary.
    await this.rulesGenerator.addAppendix();
    await this.rulesGenerator.addGlossary();
  }

  /**
   * Ajoute la pagination
   */
  private addPagination(options: PdfGeneratorOptions): void {
    if (!this.pdfDoc) {
      throw new Error("PDF document not initialized");
    }
    const pages = this.pdfDoc.getPages();
    const totalPages = pages.length;
    const font = options.fonts.regular;
    const fontSize = options.marginTopFontSize;

    pages.forEach((page: any, index: number) => {
      const pageNum = index + 1;
      const isOdd = pageNum % 2 === 1;
      const textNumber = `${pageNum}/${totalPages}`;
      const textNumberWidth = font.widthOfTextAtSize(textNumber, fontSize);
      const x = isOdd
        ? options.pageSize.width -
          textNumberWidth -
          options.pageSize.marginRight
        : options.pageSize.marginLeft;
      const y = options.pageSize.height - options.pageSize.marginTop / 2;

      page.drawText(`${pageNum}/${totalPages}`, {
        x,
        y,
        size: options.marginTopFontSize,
        font,
        color: options.marginTopColor,
      });
    });
  }

  /**
   * Ajoute la pagination
   */
  private addHeaderAndFooter(
    options: PdfGeneratorOptions,
    title: string,
    footer?: string[]
  ): void {
    if (!this.pdfDoc) {
      throw new Error("PDF document not initialized");
    }

    const pages = this.pdfDoc.getPages();
    const font = options.fonts.regular;
    const fontSize = options.marginTopFontSize;

    const titleWidth = font.widthOfTextAtSize(title, fontSize);
    const titleX = (options.pageSize.width - titleWidth) / 2;

    pages.forEach((page: any, index: number) => {
      const y = options.pageSize.height - options.pageSize.marginTop / 2;

      page.drawText(title, {
        x: titleX,
        y,
        size: options.marginTopFontSize,
        font,
        color: options.marginTopColor,
      });

      if (footer) {
        // add footer
        const footerY = options.pageSize.marginBottom / 2;
        const footerSize = options.marginBottomFontSize;

        footer.forEach((line, index) => {
          const lineWidth = font.widthOfTextAtSize(line, footerSize);
          const lineX = (options.pageSize.width - lineWidth) / 2;
          page.drawText(line.replace("{version}", options.version || ""), {
            x: lineX,
            y: footerY - index * 12,
            size: footerSize,
            font,
            color: options.marginBottomColor,
          });
        });
      }

      if (options.showBorders && options.borderWidth > 0) {
        const delta = options.borderInterval + options.borderWidth / 2;
        page.drawRectangle({
          x: options.pageSize.marginLeft - delta,
          y: options.pageSize.marginBottom - delta,
          width:
            options.pageSize.width -
            options.pageSize.marginLeft -
            options.pageSize.marginRight +
            delta * 2,
          height:
            options.pageSize.height -
            options.pageSize.marginBottom -
            options.pageSize.marginTop +
            delta * 2,
          color: rgb(0.98, 0.98, 0.98),
          opacity: 0.1,
          borderColor: rgb(0.95, 0.95, 0.95),
          borderWidth: options.borderWidth,
          borderOpacity: Math.max(0, Math.min(1, options.borderOpacity)),
        });

        page.drawLine({
          start: {
            x: options.pageSize.marginRight,
            y: options.pageSize.height - options.pageSize.marginTop,
          },
          end: {
            x: options.pageSize.marginLeft,
            y: options.pageSize.height - options.pageSize.marginTop,
          },
          thickness: 1,
          color: rgb(0.9, 0, 0),
          opacity: 0.1,
        });
      }
    });
  }

  /**
   * Télécharge le PDF généré
   */
  downloadPdf(pdfBytes: Uint8Array, filename: string = "tda-rules.pdf"): void {
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

// Export par défaut pour faciliter l'import
export default TdaPdfGenerator;
